import { Building2 } from "lucide-react";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { Ticket } from "@/modules/types";
import { getClientShowPath, getPersonShowPath } from "@/app/routing";
import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import {
  getContactEmail,
  getContactFullName,
  getContactPhoneRaw,
} from "@/modules/clients/clientShowUtils";
import { getTicketListMeta } from "@/modules/tickets/ticketListMeta";
import { TicketMetaSep } from "@/modules/tickets/TicketMetaSep";
import { CrmPhoneLink } from "@/modules/voice/CrmPhoneLink";
import {
  CompanyContextCard,
  PersonContextCard,
  formatCompanyLocation,
} from "@/modules/shared/profile";
import { cn } from "@/lib/utils";

const websiteHref = (value: string) =>
  /^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/\//, "")}`;

const websiteLabel = (value: string) => {
  const label = value.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  return label ? `${label}/` : value;
};

const SummaryRow = ({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "grid gap-0.5 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-3",
      className,
    )}
  >
    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
    <dd className="min-w-0 break-words text-sm text-foreground">{children}</dd>
  </div>
);

/** Ticket-only requester line when CRM contact is not linked yet. */
const RequesterFallback = ({
  ticket,
  email,
  phoneRaw,
  contactName,
}: {
  ticket: Ticket;
  email: string | null;
  phoneRaw: string | null;
  contactName: string | null;
}) => {
  if (!contactName && !email && !phoneRaw) return null;
  const parts: React.ReactNode[] = [];
  if (contactName) {
    parts.push(
      <span key="name" className="font-medium text-foreground">
        {contactName}
      </span>,
    );
  }
  if (email) {
    parts.push(
      <a
        key="email"
        href={`mailto:${email}`}
        className="break-all text-primary hover:underline"
      >
        {email}
      </a>,
    );
  }
  if (phoneRaw) {
    parts.push(
      <CrmPhoneLink
        key="phone"
        phone={phoneRaw}
        dealId={ticket.deal_id}
        className="text-primary hover:underline"
      />,
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <Building2 className="size-4 text-muted-foreground" />
        </div>
        <p className="flex min-w-0 flex-wrap items-center gap-y-0.5 text-xs leading-snug text-muted-foreground">
          {parts.map((part, index) => (
            <span key={index} className="inline-flex items-center">
              {index > 0 ? <TicketMetaSep /> : null}
              {part}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
};

const PanelIdentity = ({
  ticket,
  company,
  contact,
}: {
  ticket: Ticket;
  company?: Company | null;
  contact?: Contact | null;
}) => {
  const meta = getTicketListMeta(ticket, company, contact);
  const companyName = company?.name?.trim() || null;
  const contactName =
    contact && getContactFullName(contact) !== "—"
      ? getContactFullName(contact)
      : ticket.requester_name?.trim() || null;
  const email =
    meta.email ??
    (contact && getContactEmail(contact) !== "—"
      ? getContactEmail(contact)
      : null) ??
    ticket.requester_email?.trim() ??
    null;
  const phoneRaw =
    meta.phone ??
    (contact ? getContactPhoneRaw(contact) || null : null) ??
    company?.phone_number?.trim() ??
    null;
  const address = (company ? formatCompanyLocation(company) : "") || null;
  const website = meta.website ?? company?.website?.trim() ?? null;

  if (
    !companyName &&
    !contactName &&
    !email &&
    !phoneRaw &&
    !address &&
    !website
  ) {
    return null;
  }

  return (
    <section className="min-w-0 space-y-3" aria-label="Account identity">
      {company?.id != null && companyName ? (
        <CompanyContextCard
          companyId={company.id}
          name={companyName}
          website={website}
          isClient={company.is_client}
          viewProfileHref={getClientShowPath(company.id)}
          avatar={<CompanyAvatar record={company} width={40} />}
        />
      ) : null}

      {contact?.id != null ? (
        <PersonContextCard
          contact={contact}
          viewProfileHref={getPersonShowPath(contact)}
        />
      ) : (
        <RequesterFallback
          ticket={ticket}
          email={email}
          phoneRaw={phoneRaw}
          contactName={contactName}
        />
      )}

      {address ? (
        <p className="px-1 text-2xs leading-snug text-muted-foreground">
          {address}
        </p>
      ) : null}
    </section>
  );
};

export const TicketClientSummaryCard = ({
  ticket,
  company,
  contact,
  matchedFromEmail = false,
  className,
  variant = "default",
}: {
  ticket: Ticket;
  company?: Company | null;
  contact?: Contact | null;
  matchedFromEmail?: boolean;
  className?: string;
  variant?: "default" | "panel";
}) => {
  if (variant === "panel") {
    return (
      <div className={className}>
        <PanelIdentity ticket={ticket} company={company} contact={contact} />
      </div>
    );
  }

  const meta = getTicketListMeta(ticket, company, contact);
  const companyName = company?.name?.trim();
  const contactName = contact ? getContactFullName(contact) : null;
  const contactTitle = contact?.title?.trim();
  const email =
    meta.email ??
    (contact && getContactEmail(contact) !== "—"
      ? getContactEmail(contact)
      : null);
  const phoneRaw =
    meta.phone ??
    (contact ? getContactPhoneRaw(contact) || null : null) ??
    company?.phone_number?.trim();
  const address =
    (contact
      ? [
          contact.address?.trim(),
          [contact.city?.trim(), contact.state_abbr?.trim()]
            .filter(Boolean)
            .join(", "),
          contact.zipcode?.trim(),
        ]
          .filter(Boolean)
          .join(", ")
      : "") ||
    (company ? formatCompanyLocation(company) : "") ||
    null;
  const website = meta.website ?? company?.website?.trim() ?? null;

  if (
    !companyName &&
    !contactName &&
    !email &&
    !phoneRaw &&
    !address &&
    !website
  ) {
    return null;
  }

  const showSharedCompany = company?.id != null && Boolean(companyName);
  const showSharedContact = contact?.id != null;

  return (
    <section
      className={cn(
        "rounded-lg border bg-muted/25 px-3 py-3 sm:px-4",
        className,
      )}
      aria-label="Client summary"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Account details
      </p>

      <div className="mt-3 space-y-3">
        {showSharedCompany ? (
          <CompanyContextCard
            companyId={company!.id}
            name={companyName!}
            website={website}
            isClient={company!.is_client}
            viewProfileHref={getClientShowPath(company!.id)}
            avatar={<CompanyAvatar record={company!} width={40} />}
            className="border-border bg-background/50"
          />
        ) : null}

        {showSharedContact ? (
          <PersonContextCard
            contact={contact!}
            viewProfileHref={getPersonShowPath(contact!)}
            className="border-border bg-background/50"
          />
        ) : null}

        <dl className="space-y-2.5">
          {!showSharedCompany && companyName ? (
            <SummaryRow label="Account">
              <span className="font-medium">{companyName}</span>
            </SummaryRow>
          ) : null}

          {!showSharedContact && contactName && contactName !== "—" ? (
            <SummaryRow label="Contact">
              <span className="font-medium">{contactName}</span>
              {contactTitle ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {contactTitle}
                </span>
              ) : null}
            </SummaryRow>
          ) : null}

          {!showSharedContact && email ? (
            <SummaryRow label="Email">
              <a
                href={`mailto:${email}`}
                className="break-all text-primary hover:underline"
              >
                {email}
              </a>
            </SummaryRow>
          ) : null}

          {!showSharedContact && phoneRaw ? (
            <SummaryRow label="Phone">
              <CrmPhoneLink
                phone={phoneRaw}
                contactId={contact?.id}
                dealId={ticket.deal_id}
                className="text-primary hover:underline"
              />
            </SummaryRow>
          ) : null}

          {address ? (
            <SummaryRow label="Address">
              <span className="text-muted-foreground">{address}</span>
            </SummaryRow>
          ) : null}

          {!showSharedCompany && website ? (
            <SummaryRow label="Website">
              <a
                href={websiteHref(website)}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-primary hover:underline"
              >
                {websiteLabel(website)}
              </a>
            </SummaryRow>
          ) : null}
        </dl>
      </div>

      <p className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
        {matchedFromEmail
          ? "Matched an existing CRM contact from this sender's email. Save changes to link this ticket."
          : "Replies on this ticket go to the contact email above."}
      </p>
    </section>
  );
};
